import { useCallback } from 'react';
import sendExperienceAnalytics from '@atlassian/jira-common-experience-tracking-analytics/src/index.tsx';
import logger from '@atlassian/jira-common-util-logging/src/log.tsx';
import { SAVE_CUSTOM_GADGET_TITLE_PREFERENCES } from '@atlassian/jira-dashboard-common/src/constants.tsx';
import type { SaveCustomGadgetTitle } from '@atlassian/jira-dashboard-common/src/types.tsx';
import { lensGadget } from '@atlassian/jira-dashboard-internal-common/src/utils/dashboard-config-lenses/index.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { performPutRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import { useDashboardResource } from '@atlassian/jira-router-resources-dashboard/src/index.tsx';

const expAttributes = {
	analyticsSource: 'dashboard',
	experience: SAVE_CUSTOM_GADGET_TITLE_PREFERENCES,
	application: null,
	edition: null,
	additionalAttributes: {},
} as const;

export const useSaveCustomGadgetTitle = () => {
	const { data, update } = useDashboardResource();

	return useCallback<SaveCustomGadgetTitle>(
		async (id: string, title: string) => {
			const gadget = data?.gadgets.find((g) => g.id === id);
			if (!gadget || title === gadget.customisedTitle || !data) {
				return;
			}
			const changeTitleUrl = `/rest/dashboards/1.0/${data.id}/gadget/${id}/title`;

			try {
				await performPutRequest(changeTitleUrl, {
					body: JSON.stringify({ title }),
				});

				update(lensGadget(id, { customisedTitle: title }));
				sendExperienceAnalytics({
					...expAttributes,
					wasExperienceSuccesful: true,
				});
				logger.safeInfoWithoutCustomerData('spa-apps.dashboard', 'Set gadget title');
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				sendExperienceAnalytics({
					...expAttributes,
					wasExperienceSuccesful: false,
				});
				fireErrorAnalytics({
					error,
					meta: {
						id: SAVE_CUSTOM_GADGET_TITLE_PREFERENCES,
						packageName: 'jiraDashboardEdit',
					},
					sendToPrivacyUnsafeSplunk: true,
				});
				throw error;
			}
		},
		// update should be in this list, but it changes every render and breaks everything
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[data],
	);
};

export const useSaveCustomGadgetTitleById = (id: string) => {
	const saveCustomTitle = useSaveCustomGadgetTitle();

	return useCallback<(arg1: string) => Promise<void>>(
		(title) => saveCustomTitle(id, title),
		[id, saveCustomTitle],
	);
};
